import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Pencil, Wand2 } from 'lucide-react';

import { cn } from '../../../lib/cn';
import type { PlanSession } from '../../types/coursePlan';
import {
  ARCHETYPE_BADGE_CLASS,
  archetypeFormatLabel,
  resolveSessionConfig,
  shouldShowArchetypeBadge,
} from '../../lib/hitoArchetypeUi';
import { formatSessionNotes, parseSessionNotes } from '../../lib/sessionNotesHelpers';
import { extractOaCodeFromText } from '../../lib/parseSessionDetail';
import type { HitoConfigDraft } from './HitoConfigDialog';
import { HitoConfigDialog } from './HitoConfigDialog';
import { SessionDetailRenderer } from './SessionDetailRenderer';
import { SessionLinkedMaterials } from './SessionLinkedMaterials';
import { CurriculumDimensionBadge } from './CurriculumDimensionBadge';
import type { PlanUnit } from '../../types/coursePlan';

interface HitoCardProps {
  session: PlanSession;
  hitoIndex: number;
  isLast?: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onAutoExpand: () => void;
  isGenerating: boolean;
  courseId: string;
  subjectId: string;
  subjectTag: string;
  level: string;
  unit: PlanUnit;
  onUpdate: (patch: Partial<PlanSession>) => void;
  onDelete: () => void;
  onGenerateDetail: (session: PlanSession) => Promise<{ ok: boolean; error?: string }>;
}

export function HitoCard({
  session,
  hitoIndex,
  isLast = false,
  isExpanded,
  onToggleExpand,
  onAutoExpand,
  isGenerating,
  courseId,
  subjectId,
  subjectTag,
  level,
  unit,
  onUpdate,
  onDelete,
  onGenerateDetail,
}: HitoCardProps) {
  const hasGeneratedDetail = Boolean(session.detail?.trim());
  const [configOpen, setConfigOpen] = useState(false);
  const prevDetailRef = useRef(session.detail);

  const config = resolveSessionConfig(session);
  const showBadge = shouldShowArchetypeBadge(session);
  const sessionOaCode = extractOaCodeFromText(parseSessionNotes(session.notes).foco);

  useEffect(() => {
    const prev = prevDetailRef.current?.trim() ?? '';
    const next = session.detail?.trim() ?? '';
    if (!prev && next) {
      onAutoExpand();
    }
    prevDetailRef.current = session.detail;
  }, [session.detail, onAutoExpand]);

  const openConfig = (event: React.MouseEvent) => {
    event.stopPropagation();
    setConfigOpen(true);
  };

  const handleGenerateFromDialog = async (draft: HitoConfigDraft) => {
    const mergedSession: PlanSession = {
      ...session,
      title: draft.title.trim(),
      notes: formatSessionNotes(draft.foco, draft.evidencia),
      plannedDate: draft.plannedDate,
      config: draft.config,
    };
    return onGenerateDetail(mergedSession);
  };

  return (
    <>
      <li className={cn('relative pb-6', isLast ? 'pb-0' : '')}>
        <span
          className="absolute -left-[calc(1rem+5px)] top-5 h-2.5 w-2.5 rounded-full border-2 border-gray-900 bg-white"
          aria-hidden
        />

        <article
          className={cn(
            'rounded-xl border bg-white shadow-sm transition-all duration-200',
            isExpanded
              ? 'overflow-visible border-gray-900 shadow-md'
              : 'overflow-hidden border-gray-200 hover:border-gray-300 hover:shadow-md',
          )}
        >
          <div
            role="button"
            tabIndex={0}
            className="flex w-full cursor-pointer items-center gap-3 px-5 py-4 text-left transition-colors"
            onClick={onToggleExpand}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onToggleExpand();
              }
            }}
            aria-expanded={isExpanded}
          >
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400">
                Hito {hitoIndex + 1}
              </p>
              <h3 className="mt-1 truncate text-base font-semibold leading-snug text-gray-900">
                {session.title.trim() || 'Sin título'}
              </h3>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {session.curriculumSlice ? (
                <CurriculumDimensionBadge slice={session.curriculumSlice} />
              ) : null}

              {showBadge ? (
                <span className={cn('rounded-md px-2 py-0.5 text-[10px]', ARCHETYPE_BADGE_CLASS)}>
                  {archetypeFormatLabel(config)}
                </span>
              ) : null}

              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-50"
                onClick={openConfig}
                aria-label="Configurar hito"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>

              <ChevronDown
                className={cn(
                  'h-4 w-4 text-gray-400 transition-transform duration-200',
                  isExpanded ? 'rotate-180' : '',
                )}
                aria-hidden
              />
            </div>
          </div>

          <div
            className={cn(
              'grid transition-all duration-200 ease-out',
              isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
            )}
          >
            <div className={cn('min-h-0', isExpanded ? 'overflow-visible' : 'overflow-hidden')}>
              {isExpanded ? (
                <div className="animate-in fade-in slide-in-from-top-2 w-full border-t border-[var(--sala-border-strong)] px-6 pb-8 pt-6 duration-200 ease-out">
                  {session.curriculumSlice ? (
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                      <CurriculumDimensionBadge slice={session.curriculumSlice} />
                      <span className="text-[11px] text-gray-500">
                        Dimensión dominante de este hito en la ruta tridimensional.
                      </span>
                    </div>
                  ) : null}

                  {hasGeneratedDetail ? (
                    <SessionDetailRenderer
                      rawContent={session.detail!}
                      oaCode={sessionOaCode}
                      linkedMaterialsFooter={
                        <SessionLinkedMaterials
                          courseId={courseId}
                          subjectId={subjectId}
                          subjectTag={subjectTag}
                          level={level}
                          unit={unit}
                          session={session}
                        />
                      }
                    />
                  ) : (
                    <>
                      <div className="py-12 text-center">
                        <Wand2
                          className="mx-auto h-9 w-9 text-gray-400"
                          strokeWidth={1.5}
                          aria-hidden
                        />
                        <p className="mt-4 text-sm font-semibold text-gray-900">
                          Sin detalle de clase
                        </p>
                        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-500">
                          Configura el foco y el arquetipo para que la IA diseñe la línea de tiempo de
                          esta sesión.
                        </p>
                        <button
                          type="button"
                          className="mt-6 inline-flex items-center rounded-lg bg-gray-900 px-4 py-2.5 text-xs font-medium text-white transition hover:bg-gray-800"
                          onClick={(event) => {
                            event.stopPropagation();
                            setConfigOpen(true);
                          }}
                        >
                          <Wand2 className="mr-1.5 h-3.5 w-3.5" />
                          Configurar y Generar IA
                        </button>
                      </div>
                      <SessionLinkedMaterials
                        courseId={courseId}
                        subjectId={subjectId}
                        subjectTag={subjectTag}
                        level={level}
                        unit={unit}
                        session={session}
                      />
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </article>
      </li>

      <HitoConfigDialog
        open={configOpen}
        onOpenChange={setConfigOpen}
        session={session}
        hitoIndex={hitoIndex}
        isGenerating={isGenerating}
        subjectTag={subjectTag}
        onSave={onUpdate}
        onGenerate={handleGenerateFromDialog}
        onDelete={onDelete}
      />
    </>
  );
}
